import { Injectable } from '@angular/core';

/**
 * Service for normalizing HTML content for email rendering.
 * Email clients often strip <div> and collapse whitespace, which can turn multi-line content into a single line.
 * This service ensures line breaks survive email client sanitization by converting block boundaries to <br> tags.
 */
@Injectable({
  providedIn: 'root'
})
export class    EmailHtmlNormalizerService {

  constructor() { }

  /**
   * Normalizes HTML for email clients by:
   * 1. Converting <p> tags to <div> to avoid email client paragraph spacing
   * 2. Converting newline characters in text nodes to explicit <br> elements
   * 3. Converting empty blocks to <br> tags
   * 4. Normalizing list spacing
   * 5. Ensuring <br> exists between consecutive simple line blocks
   * 
   * @param input The HTML string to normalize
   * @returns Normalized HTML string safe for email rendering
   */
  normalizeHtmlForEmail(input: string): string {
    let html = (input || '').toString();
    if (!html.trim()) return html;

    // If editor ever provides plain text with newlines, convert to <br>.
    const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(html);
    if (!looksLikeHtml) {
      return html.replace(/\r\n|\r|\n/g, '<br>');
    }

    // Robust path: parse HTML and normalize line breaks in the DOM.
    // Many email gateways strip <div>/<p> or collapse raw newlines, so we ensure <br> nodes exist.
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const body = doc.body;

      // Normalize: some email clients add margins/spacing for <p> and can create
      // visually "empty" gaps. Convert all <p> to <div> while preserving attributes
      // and children.
      try {
        const paragraphs = Array.from(body.querySelectorAll('p')) as HTMLParagraphElement[];
        for (const p of paragraphs) {
          const div = doc.createElement('div');
          try {
            for (const attr of Array.from(p.attributes)) {
              div.setAttribute(attr.name, attr.value);
            }
          } catch (e) {
            // ignore
          }

          // Move children to preserve DOM nodes (incl. <br>, links, spans, etc.)
          while (p.firstChild) {
            div.appendChild(p.firstChild);
          }

          try {
            p.replaceWith(div);
          } catch (e) {
            // ignore
          }
        }
      } catch (e) {
        // ignore
      }

      // 1) Convert newline characters in text nodes into explicit <br> elements.
      const textNodes: Text[] = [];
      const walker = doc.createTreeWalker(body, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        const t = walker.currentNode as Text;
        if ((t.nodeValue || '').match(/\r|\n/)) textNodes.push(t);
      }

      for (const t of textNodes) {
        const raw = (t.nodeValue || '');
        const parts = raw.split(/\r\n|\r|\n/);
        if (parts.length <= 1) continue;

        const frag = doc.createDocumentFragment();
        for (let i = 0; i < parts.length; i++) {
          const part = parts[i];
          if (part) frag.appendChild(doc.createTextNode(part));
          if (i < parts.length - 1) frag.appendChild(doc.createElement('br'));
        }
        try {
          t.parentNode?.replaceChild(frag, t);
        } catch (e) {
          // ignore
        }
      }

 

      // Many email clients apply default margins/padding to lists.
      // Normalize <ul>/<ol> spacing so templates look consistent.
      try {
        const lists = Array.from(body.querySelectorAll('ul, ol')) as HTMLElement[];
        for (const list of lists) {
          try {
            const existing = (list.getAttribute('style') || '').trim();
            const reset = 'margin:0;padding-left:18px;';
            // Keep existing styles, but ensure our reset is applied.
            // (padding-left kept non-zero so bullets remain indented.)
            list.setAttribute('style', existing ? `${existing};${reset}` : reset);
          } catch (e) {
            // ignore
          }
        }
      } catch (e) {
        // ignore
      }

      // Normalize images for email clients (Gmail, Outlook, etc.)
      try {
        // 1) Normalize .image-block structures
        const imageBlocks = Array.from(body.querySelectorAll('.image-block')) as HTMLElement[];
        for (const block of imageBlocks) {
          const img = block.querySelector('img') as HTMLImageElement | null;
          if (!img) continue;

          let w = parseFloat(block.getAttribute('data-image-w') || '0');
          let h = parseFloat(block.getAttribute('data-image-h') || '0');
          if (!w || !h || w <= 120) {
            const frame = block.querySelector('.image-frame') as HTMLElement | null;
            if (frame) {
              const fw = parseFloat(frame.style.width) || parseFloat(frame.offsetWidth as any) || 0;
              const fh = parseFloat(frame.style.height) || parseFloat(frame.offsetHeight as any) || 0;
              if (fw > 120) w = fw;
              if (fh > 0) h = fh;
            }
          }
          if (!w || w <= 120) {
            const styleW = parseFloat(img.style.width || '0');
            const attrW = parseFloat(img.getAttribute('width') || '0');
            if (styleW > 120) w = styleW;
            else if (attrW > 120) w = attrW;
            else if (w <= 120) w = 0; // Don't force small dummy 100px/120px width
          }
          if (!h || h <= 120) {
            const styleH = parseFloat(img.style.height || '0');
            const attrH = parseFloat(img.getAttribute('height') || '0');
            if (styleH > 0) h = styleH;
            else if (attrH > 0) h = attrH;
            else h = 0;
          }

          let align = block.getAttribute('data-image-align') || 'left';
          if (block.classList.contains('align-center')) align = 'center';
          else if (block.classList.contains('align-right')) align = 'right';

          if (w > 0) img.setAttribute('width', String(Math.round(w)));
          else img.removeAttribute('width');

          if (h > 0) img.setAttribute('height', String(Math.round(h)));
          else img.removeAttribute('height');

          const imgStyle = `display:inline-block;${w > 0 ? `width:${Math.round(w)}px;` : ''}max-width:100%;height:auto;`;
          img.setAttribute('style', imgStyle);
          img.removeAttribute('contenteditable');

          const wrapDiv = doc.createElement('div');
          let textAlign = 'left';
          if (align === 'center') textAlign = 'center';
          else if (align === 'right') textAlign = 'right';
          wrapDiv.setAttribute('style', `text-align:${textAlign};margin:10px 0;`);

          const newImg = img.cloneNode(true) as HTMLImageElement;
          wrapDiv.appendChild(newImg);
          block.replaceWith(wrapDiv);
        }

        // 2) Normalize any remaining standalone img elements
        const standaloneImgs = Array.from(body.querySelectorAll('img')) as HTMLImageElement[];
        for (const img of standaloneImgs) {
          let styleStr = img.getAttribute('style') || '';
          let w = parseFloat(img.getAttribute('width') || '0');
          if (!w && styleStr.includes('width:')) {
            const match = styleStr.match(/width:\s*(\d+)px/i);
            if (match) w = parseFloat(match[1]);
          }

          if (w <= 120) w = 0; // Prevent tiny dummy widths

          let cleanStyle = 'max-width:100%;height:auto;';
          if (w > 0) {
            cleanStyle = `width:${Math.round(w)}px;max-width:100%;height:auto;`;
            img.setAttribute('width', String(Math.round(w)));
          } else {
            img.removeAttribute('width');
          }
          if (styleStr.includes('display: block')) {
            cleanStyle += 'display:block;';
          } else {
            cleanStyle += 'display:inline-block;';
          }
          img.setAttribute('style', cleanStyle);
        }
      } catch (e) {
        // ignore
      }

      // Normalize tables for email clients (Gmail, Outlook, etc.)
      try {
        const tables = Array.from(body.querySelectorAll('table')) as HTMLTableElement[];
        for (const table of tables) {
          let tableStyle = (table.getAttribute('style') || '').trim();
          if (!tableStyle.includes('border-collapse')) {
            tableStyle = `border-collapse:collapse;${tableStyle}`;
          }

          let widthAttr = table.getAttribute('width');
          let widthStyle = table.style.width;
          if (!widthAttr && widthStyle) {
            table.setAttribute('width', widthStyle);
          } else if (!widthStyle && widthAttr) {
            tableStyle += `;width:${widthAttr}${widthAttr.endsWith('%') || widthAttr.endsWith('px') ? '' : 'px'};`;
          } else if (!widthAttr && !widthStyle) {
            table.setAttribute('width', '100%');
            tableStyle += ';width:100%;';
          }

          let alignAttr = table.getAttribute('align');
          let marginLeft = table.style.marginLeft;
          let marginRight = table.style.marginRight;

          if (!alignAttr) {
            if (marginLeft === 'auto' && marginRight === 'auto') {
              alignAttr = 'center';
            } else if (marginLeft === 'auto') {
              alignAttr = 'right';
            } else {
              alignAttr = 'left';
            }
          }
          table.setAttribute('align', alignAttr);

          if (alignAttr === 'center') {
            if (!tableStyle.includes('margin-left')) tableStyle += ';margin-left:auto;margin-right:auto;';
          } else if (alignAttr === 'right') {
            if (!tableStyle.includes('margin-left')) tableStyle += ';margin-left:auto;';
          }

          if (!table.getAttribute('cellpadding')) table.setAttribute('cellpadding', '8');
          if (!table.getAttribute('cellspacing')) table.setAttribute('cellspacing', '0');

          let borderWidth = table.style.borderWidth || '1px';
          let borderStyle = table.style.borderStyle || 'solid';
          let borderColor = table.style.borderColor || '#dddddd';

          if (table.style.border && table.style.border !== 'none') {
            const parts = table.style.border.split(' ');
            if (parts.length >= 3) {
              borderWidth = parts[0];
              borderStyle = parts[1];
              borderColor = parts.slice(2).join(' ');
            }
          }

          const hasNoBorder = borderStyle === 'none' || borderWidth === '0' || borderWidth === '0px' || table.getAttribute('border') === '0';
          if (!hasNoBorder) {
            table.setAttribute('border', borderWidth.replace('px', ''));
          } else {
            table.setAttribute('border', '0');
          }

          table.setAttribute('style', tableStyle);

          const cells = Array.from(table.querySelectorAll('td, th')) as HTMLTableCellElement[];
          for (const cell of cells) {
            let cellBorderWidth = cell.style.borderWidth || borderWidth;
            let cellBorderStyle = cell.style.borderStyle || borderStyle;
            let cellBorderColor = cell.style.borderColor || borderColor;

            if (cell.style.border && cell.style.border !== 'none') {
              const parts = cell.style.border.split(' ');
              if (parts.length >= 3) {
                cellBorderWidth = parts[0];
                cellBorderStyle = parts[1];
                cellBorderColor = parts.slice(2).join(' ');
              }
            }

            const isCellNoBorder = cellBorderStyle === 'none' || cellBorderWidth === '0' || cellBorderWidth === '0px' || hasNoBorder;

            if (isCellNoBorder) {
              cell.style.border = 'none';
            } else {
              const cellBorderVal = `${cellBorderWidth.endsWith('px') ? cellBorderWidth : cellBorderWidth + 'px'} ${cellBorderStyle} ${cellBorderColor}`;
              cell.style.border = cellBorderVal;
              cell.style.borderWidth = cellBorderWidth.endsWith('px') ? cellBorderWidth : cellBorderWidth + 'px';
              cell.style.borderStyle = cellBorderStyle;
              cell.style.borderColor = cellBorderColor;
            }

            if (!cell.style.padding) {
              cell.style.padding = '8px';
            }
          }
        }
      } catch (e) {
        // ignore
      }

      // 3) Ensure <br> exists between consecutive simple line blocks (<div>),
      // so line breaks survive even if the email client strips block semantics.
      const isSimpleLineBlock = (node: Element): boolean => {
        if (!node) return false;
        const tag = node.tagName.toUpperCase();
        if (tag !== 'DIV') return false;
        // If it contains complex block structures, don't treat it as a simple line.
        return !node.querySelector('table,ul,ol,blockquote,pre,h1,h2,h3,h4,h5,h6');
      };

      const isWhitespaceTextNode = (n: Node | null): boolean => {
        try {
          if (!n || n.nodeType !== Node.TEXT_NODE) return false;
          return ((n.nodeValue || '').replace(/\u00a0/g, ' ').trim().length === 0);
        } catch (e) {
          return false;
        }
      };

      const lastMeaningfulChild = (el: Element): Node | null => {
        try {
          let n: Node | null = el.lastChild;
          while (n && isWhitespaceTextNode(n)) n = n.previousSibling;
          return n;
        } catch (e) {
          return el.lastChild;
        }
      };

      const firstMeaningfulChild = (el: Element): Node | null => {
        try {
          let n: Node | null = el.firstChild;
          while (n && isWhitespaceTextNode(n)) n = n.nextSibling;
          return n;
        } catch (e) {
          return el.firstChild;
        }
      };

      const hasTrailingBr = (el: Element): boolean => {
        const n = lastMeaningfulChild(el);
        return !!(n && n.nodeType === Node.ELEMENT_NODE && (n as Element).tagName.toUpperCase() === 'BR');
      };

      const hasLeadingBr = (el: Element): boolean => {
        const n = firstMeaningfulChild(el);
        return !!(n && n.nodeType === Node.ELEMENT_NODE && (n as Element).tagName.toUpperCase() === 'BR');
      };

      const ensureBrBetweenSiblings = (container: Element, insertBetweenSiblings: boolean) => {
        let child = container.firstChild;
        while (child) {
          const next = child.nextSibling;

          if (child.nodeType === Node.ELEMENT_NODE) {
            const el = child as Element;
            const tag = el.tagName.toUpperCase();
            
            // Recurse to handle nested wrappers some editors produce.
            try { ensureBrBetweenSiblings(el, true); } catch (e) {}

            if (insertBetweenSiblings && isSimpleLineBlock(el) && next) {
              // Check if next sibling is already a <br> tag
              const nextIsBr =
                next.nodeType === Node.ELEMENT_NODE &&
                (next as Element).tagName.toUpperCase() === 'BR';
              
              // Check if there's whitespace followed by a <br>
              let hasBrAfterWhitespace = false;
              if (!nextIsBr && next && next.nodeType === Node.TEXT_NODE) {
                const afterWhitespace = next.nextSibling;
                hasBrAfterWhitespace = !!(afterWhitespace && 
                  afterWhitespace.nodeType === Node.ELEMENT_NODE &&
                  (afterWhitespace as Element).tagName.toUpperCase() === 'BR' &&
                  isWhitespaceTextNode(next));
              }
              
              // Avoid inserting a double break when the current block already ends with <br>
              // or the next block begins with <br> (common contenteditable artifacts).
              const nextEl = (next && next.nodeType === Node.ELEMENT_NODE ? (next as Element) : null);
              const avoidDouble = hasTrailingBr(el) || (nextEl ? hasLeadingBr(nextEl) : false) || hasBrAfterWhitespace;
              
              if (!nextIsBr && !avoidDouble) {
                try {
                  container.insertBefore(doc.createElement('br'), next);
                } catch (e) {}
              }
            }
          }

          child = next;
        }
      };

      // Do not automatically inject <br> tags between divs.
      // Preserve the existing HTML structure as-is to avoid unwanted line breaks.
      // ensureBrBetweenSiblings(body, false);

      // Compose preview (contenteditable) can visually collapse top-level <br> runs.
      // To make explicit blank lines show reliably, wrap top-level <br> into a block.
      // This keeps list structure intact while improving preview fidelity.
      try {
        const children = Array.from(body.childNodes);
        for (const n of children) {
          if (n.nodeType === Node.ELEMENT_NODE && (n as Element).tagName.toUpperCase() === 'BR') {
            const wrap = doc.createElement('div');
            wrap.appendChild(doc.createElement('br'));
            try {
              (n as Element).replaceWith(wrap);
            } catch (e) {
              // ignore
            }
          }
        }
      } catch (e) {
        // ignore
      }

      // Do NOT flatten the DOM into a single <br>-joined string here.
      // Flattening breaks list/block structure and can produce the "all inline + <br>" format.
      // Returning body.innerHTML preserves <ul>/<li>/<div> blocks for better email rendering.
      return body.innerHTML;
    } catch (e) {
      // Fallback: conservative regex boundary insertion.
      try {
        // Convert paragraph tags to divs to avoid paragraph spacing in email clients.
        html = html.replace(/<\s*p(\b[^>]*)>/gi, '<div$1>');
        html = html.replace(/<\s*\/\s*p\s*>/gi, '</div>');
        html = html.replace(/<\/div>\s*(?!<br\s*\/?>)\s*<div\b/gi, '</div><br><div');
      } catch (e2) {}
      return html;
    }
  }
}
